import { store } from './store';
import actions from './datax/actions/navigation';
import { NavigationState } from './datax/reducers/navigation';
import { CardLabel } from './datax/types';

// Route URLs
export const Routes = {
    WELCOME: '/superset/welcome/',
    CHART_LIST: '/chart/list/',
    DASHBOARD_LIST: '/dashboard/list/',
    DATASET_LIST: '/tablemodelview/list/',
    DATABASE_LIST: '/databaseview/list/',
    NEW_CHART: '/chart/add',
    NEW_DASHBOARD: '/dashboard/new',
    SQLLAB: '/superset/sqllab?new=true',
    ALERTS: '/alert/list',
    USER_LIST: '/users/list/',
    ROLES_LIST: '/roles/list/',
    RLS: '/rowlevelsecurityfiltersmodelview/list/',
    ACTION_LOG: '/logmodelview/list/',
    ANNOTATION: '/annotationlayermodelview/list/',
    USER: '/users/userinfo/',
    PROFILE: '/superset/profile/',
    LOGOUT: '/logout'
};

// Navigation Exception
class NavigationError extends Error {
    constructor(msg: string) {
        super(msg);

        // Set the prototype explicitly.
        Object.setPrototypeOf(this, NavigationError.prototype);
    }
}

/**
 * All Routes
 * 1. New chart: /chart/add
 * 2. New Dashboard: /dashboard/new
 * 3. SQL lab: /superset/sqllab?new=true
 * 4. Alerts & Reports: /alert/list
 * 5. Chartlist: /chart/list/
 * 6. Dashboard List: /dashboard/list/
 * 7. Dataset List: /tablemodelview/list/
 * 8. Database List: /databaseview/list/
 */

/**
 * Get navigation history from session store else start from empty history.
 */
const getNavigationSessionStore = () => {
    try {
        const storedValue = sessionStorage.getItem('dvf-navigation');

        if (storedValue) {
            const state = JSON.parse(storedValue) as NavigationState;
            initNavigation(state.routeStack);
        } else {
            initNavigation([]);
        }
    } catch (ex) {
        console.log(ex);
    }
}

/**
 * Save Navigation history to session store.
 * @param state Current state to save  
 */
export const saveNavigationSessionStore = (state: NavigationState) => {
    try {
        sessionStorage.setItem('dvf-navigation', JSON.stringify(state));
    } catch (ex) {
        console.log(ex);
        sessionStorage.removeItem('dvf-navigation');
    }
}

/**
 * Initialize navigation history stack.
 * @param routes Array of routes(url paths) to be used for initialization
 */
export const initNavigation = (routes: string[]) => {
    try {    
        store.dispatch(actions.init(routes));
    } catch (ex) {
        console.log(ex);
        throw new NavigationError(`Unable to initialize Navigation State! Routes ${routes} ! Err: ${ex}`);
    }
}

/**
 * Navigate to next route and add to stack.
 * @param route url path
 */
export const navigateTo = (route: string) => {
    try {
        getNavigationSessionStore();

        const state = store.getState().navigation;
        if (state && state.routeStack.length > 0) {
            if (state.routeStack[state.routeStack.length - 1] !== route) {
                store.dispatch(actions.push(route));
            }
            // don't push if navigating to same route again
        }
        else {
            store.dispatch(actions.push(route));
        }

        gotoPage(store.getState().navigation, route);
    } catch (ex) {
        console.log(ex);
        throw new NavigationError(`Unable to Navigate To ${route} ! Err: ${ex}`);
    }
}

/**
 * Navigate back to last route in stack(history) and pop that from stack.
 * In case there is no history, route to a predefined route.
 */
export const navigateBack = () => {
    try {
        getNavigationSessionStore();

        const state = store.getState().navigation;
        if (state && state.routeStack.length > 0) {
            store.dispatch(actions.pop());
            const newState = store.getState().navigation;
            if (newState.routeStack.length > 0) {
                gotoPage(store.getState().navigation,
                    newState.routeStack[newState.routeStack.length - 1]);
            }
            else {
                const newRoute = getPredefinedBackRoute(window.location.pathname);
                gotoPage(store.getState().navigation, newRoute);
            }
        }
        else {
            const newRoute = getPredefinedBackRoute(window.location.pathname);
            gotoPage(store.getState().navigation, newRoute);
        }        
    } catch (ex) {
        console.log(ex);
        throw new NavigationError(`Unable to Navigate Back! Err: ${ex}`);
    }
}

/**
 * Replace last route in stack(history) with a new route.
 * In case of no history, the route is added to stack.
 * @param route url path
 */
export const replaceLastRoute = (route: string) => {
    try {
        getNavigationSessionStore();

        const state = store.getState().navigation;
        if (state && state.routeStack.length > 0) {
            store.dispatch(actions.replace(route));
        }
        else {
            initNavigation([route]);
        }

        saveNavigationSessionStore(store.getState().navigation);
    } catch (ex) {
        console.log(ex);
        throw new NavigationError(`Unable to Replace Last Route ${route} ! Err: ${ex}`);
    }
}

/**
 * Goto route without affecting history(navigation stack).
 * Mostly used for reload/refresh purposes.
 * @param route url path
 */
export const reloadRoute = (route: string) => {
    window.location.assign(route);
}

/**
 * Add route to navigation stack.
 * @param route url path
 */
export const addRoute = (route: string) => {
    try {
        getNavigationSessionStore();

        store.dispatch(actions.push(route));

        saveNavigationSessionStore(store.getState().navigation);
    } catch (ex) {
        console.log(ex);
        throw new NavigationError(`Unable to Add Route ${route} ! Err: ${ex}`);
    }
}

/**
 * Remove/pop last route from stack(history), if present.
 */
export const removeLastRoute = () => {
    try {
        getNavigationSessionStore();

        const state = store.getState().navigation;
        if (state && state.routeStack.length > 0) {
            store.dispatch(actions.pop());
        }

        saveNavigationSessionStore(store.getState().navigation);
    } catch (ex) {
        console.log(ex);
        throw new NavigationError(`Unable to Remove Last Route ! Err: ${ex}`);
    }
}

/**
 * Open route url.
 * Save history in session store.
 * Notify iframe parent with new route.
 * Finally Navigate to new route.
 * @param state Current navigation state
 * @param url url path
 */
const gotoPage = (state: NavigationState, url: string) => {
    saveNavigationSessionStore(state);
    notifyParent(url);
    window.location.assign(url);
    // history.push(url, state);
}

/**
 * Notify parent of iframe, if any, of new route.
 * @param url url path
 */
const notifyParent = (url: string) => {
    try {
        window.top?.postMessage(url.replace('superset', 'dvf'), '*');
    } catch {}
}

/**
 * Get a predefined route based on the current route.
 * @param route url path
 * @returns predefined back url path
 */
const getPredefinedBackRoute = (route: string): string => {
    if (route.startsWith(Routes.CHART_LIST) || route.startsWith(Routes.DASHBOARD_LIST)
        || route.startsWith(Routes.DATASET_LIST) || route.startsWith(Routes.DATABASE_LIST)) {
            return Routes.WELCOME;
    }
    if (route.startsWith(Routes.NEW_CHART) || route.startsWith(Routes.NEW_DASHBOARD)) {
        return Routes.WELCOME;
    }
    if (route.startsWith(Routes.SQLLAB) || route.startsWith(Routes.ALERTS)) {
        return Routes.WELCOME;
    }
    if (route.startsWith('/alert/')) {
        return Routes.ALERTS;
    }
    if (route.startsWith('/superset/explore/?form_data={"slice_id"')) {
        return Routes.CHART_LIST;
    }
    if (route.startsWith('/superset/dashboard/')) {
        return Routes.DASHBOARD_LIST;
    }
    return Routes.WELCOME;
}

/**
 * Get Chart explore page route.
 * @param id chart id
 * @returns url path for chart explore page
 */
export const getChartExploreRoute = (id: string): string => {
    return '/superset/explore/?form_data={"slice_id":' + id + '}';
}

/**
 * Get Dashboard page route.
 * @param id dashboard id
 * @returns url path for dashboard page
 */
export const getDashboardRoute = (id: string): string => {
    return '/superset/dashboard/' + id + '/';
}

/**
 * Get Welcome page url path with tab.
 * @param tab tab to be active by default in welcome page
 * @returns url path for welcome page with tab
 */
export const getWelcomeRouteWithTab = (tab: CardLabel): string => {
    return `${Routes.WELCOME}?tab=${tab}`;
}

/**
 * Get tab from welcome route.
 * @param path url path with query/search params
 * @returns tab mentioned in `path` else Dashboard as default active tab
 */
export const getTabFromWelcomeRoute = (path: string): CardLabel => {
    try {
        const params = new URLSearchParams(path);
        return (params.get('tab') || CardLabel.Dashboard) as CardLabel;
    } catch {};
    return CardLabel.Dashboard;
}
